import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Calendar } from 'lucide-react';

const HolidayClosurePopup = () => {
  const [isVisible, setIsVisible] = useState(false);

  // February 2026 closure dates
  const POPUP_REMOVAL_DATE = new Date('2026-03-02T00:00:00');

  useEffect(() => {
    const now = new Date();

    // Auto-remove: Don't show popup after March 2, 2026
    if (now >= POPUP_REMOVAL_DATE) {
      return;
    }

    // Check if user has dismissed the popup within the last hour
    const dismissedTime = localStorage.getItem('februaryClosurePopupDismissed');
    if (dismissedTime) {
      const hourInMs = 60 * 60 * 1000; // 1 hour in milliseconds
      const timeSinceDismissed = now.getTime() - parseInt(dismissedTime, 10);
      if (timeSinceDismissed < hourInMs) {
        return;
      }
    }

    // Show popup after a short delay for better UX
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    // Save timestamp when user dismissed it
    localStorage.setItem('februaryClosurePopupDismissed', Date.now().toString());
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop - z-[9999] to be above everything including header */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] animate-fadeIn"
        onClick={handleClose}
      />

      {/* Modal Container - z-[10000] to be above backdrop */}
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-2 sm:p-4 pointer-events-none overflow-y-auto">
        <div
          className="relative bg-gradient-to-br from-amber-800 via-amber-700 to-orange-800 rounded-xl sm:rounded-2xl shadow-2xl max-w-lg w-full mx-2 my-4 pointer-events-auto animate-scaleIn overflow-hidden max-h-[90vh] sm:max-h-none"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 text-white/70 hover:text-white transition-colors z-10 p-1.5 sm:p-1 rounded-full hover:bg-white/10 bg-white/5"
            aria-label="Close popup"
          >
            <X size={20} className="sm:w-6 sm:h-6" />
          </button>

          {/* Content - scrollable on mobile */}
          <div className="relative p-4 sm:p-6 md:p-8 text-center overflow-y-auto max-h-[85vh] sm:max-h-none">
            {/* Header icon */}
            <div className="flex items-center justify-center mb-4">
              <div className="bg-white/20 rounded-full p-3">
                <AlertCircle className="text-yellow-300 w-8 h-8 sm:w-10 sm:h-10" />
              </div>
            </div>

            {/* Main heading */}
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 font-exo">
              Temporary Closure Notice
            </h2>

            {/* Closure notice */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 border border-white/20">
              <p className="text-white text-base sm:text-lg mb-4 leading-relaxed">
                Umoona Opal Mine & Museum is currently <span className="font-bold text-yellow-300">closed</span> for the month of February 2026.
              </p>

              <div className="bg-green-800/50 rounded-lg p-4 flex items-center justify-center gap-3">
                <Calendar className="text-green-300 w-6 h-6" />
                <div>
                  <p className="text-white/80 text-sm">We will reopen to the public on:</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-300">
                    2 March 2026
                  </p>
                </div>
              </div>
            </div>

            {/* Thank you message */}
            <p className="text-white/80 text-sm mb-4 sm:mb-6">
              Thank you for your understanding. We look forward to seeing you in March!
            </p>

            {/* Close button */}
            <button
              onClick={handleClose}
              className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-gray-900 font-bold py-2.5 sm:py-3 px-6 sm:px-8 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 text-sm sm:text-base"
            >
              Got it, thanks!
            </button>
          </div>
        </div>
      </div>

      {/* Custom animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        .animate-scaleIn {
          animation: scaleIn 0.4s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default HolidayClosurePopup;
